import api from '@crm-src/lib/api';

// Base API response interfaces
export interface ApiResponse<T> {
  data: T;
  message?: string;
  status?: string;
}

interface PaginatedResponse<T> {
  data: T[];
  total_count: number;
  meta: {
    pagination: {
      current_page: number;
      total_pages: number;
      total_count: number;
      per_page: number;
    };
  };
}

// Contact List interface
export interface ContactList {
  id: number;
  name: string;
  description: string | null;
  list_type: 'static' | 'dynamic' | 'smart';
  contacts_count: number;
  companies_count: number;
  members_count: number;
  marketing_list: boolean;
  gdpr_compliant: boolean;
  active: boolean;
  system_list: boolean;
  owner: {
    id: number;
    full_name: string;
    email: string;
  } | null;
  created_by: {
    id: number;
    full_name: string;
    email: string;
  } | null;
  created_at: string;
  updated_at: string;
  /**
   * The SERVER's verdict: may the viewer edit/delete this group or change its
   * members? An actor that sees all records lists every group but manages only
   * its own unless it is an admin (crm-api `require_contact_list_manager!`).
   * Optional because only the contact-groups endpoints send it — read it
   * through `canManageGroup`, never directly.
   */
  can_manage?: boolean;
}

// Contact List Member interface
export interface ContactListMember {
  id: number;
  member_type: 'Contact' | 'Customer' | 'Lead';
  member_id: number;
  name: string;
  email: string | null;
  company: string | null;
  status: 'active' | 'inactive' | 'unsubscribed';
  source: string | null;
  joined_at: string;
  left_at: string | null;
  created_at: string;
  added_by: {
    id: number;
    full_name: string;
    email: string;
  } | null;
}

// Contact List form data
export interface ContactListFormData {
  name: string;
  description?: string;
  list_type?: 'static' | 'dynamic' | 'smart';
  marketing_list?: boolean;
  gdpr_compliant?: boolean;
  active?: boolean;
}

// Contact List filters
export interface ContactListFilters {
  search?: string;
  active?: boolean;
  marketing_list?: boolean;
  /** Facet: owner user ids, comma-separated ("Owner" in the table). */
  owner_id?: string;
  sort?: string;
  /**
   * Canonical direction param. `direction` below is the legacy spelling the
   * email target selector and the Audience Builder still send; `Sortable`
   * accepts both, so neither caller had to change.
   */
  dir?: 'asc' | 'desc';
  direction?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

// Member filters
export interface MemberFilters {
  search?: string;
  status?: 'active' | 'inactive' | 'unsubscribed';
  member_type?: 'Contact' | 'Customer' | 'Lead';
  page?: number;
  per_page?: number;
}

// Member to add
export interface MemberToAdd {
  type: 'contact' | 'customer' | 'lead';
  id: number;
}

// Contact Lists API class
export class ContactListsAPI {
  // Get all contact lists with filters
  static async getAll(filters?: ContactListFilters): Promise<PaginatedResponse<ContactList>> {
    const params = new URLSearchParams();

    if (filters?.search) params.append('search', filters.search);
    if (filters?.active !== undefined) params.append('active', filters.active.toString());
    if (filters?.marketing_list !== undefined) params.append('marketing_list', filters.marketing_list.toString());
    if (filters?.owner_id) params.append('owner_id', filters.owner_id);
    if (filters?.sort) params.append('sort', filters.sort);
    if (filters?.dir) params.append('dir', filters.dir);
    if (filters?.direction) params.append('direction', filters.direction);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());

    const response = await api.get(`/contact_lists?${params.toString()}`);
    return response.data;
  }

  // Create a new contact list
  static async create(data: ContactListFormData): Promise<ApiResponse<ContactList>> {
    const response = await api.post('/contact_lists', { contact_list: data });
    return response.data;
  }

  // Update a contact list
  static async update(id: number, data: ContactListFormData): Promise<ApiResponse<ContactList>> {
    const response = await api.patch(`/contact_lists/${id}`, { contact_list: data });
    return response.data;
  }

  // Delete a contact list
  static async delete(id: number): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete(`/contact_lists/${id}`);
    return response.data;
  }

  // Get members of a contact list
  static async getMembers(id: number, filters?: MemberFilters): Promise<PaginatedResponse<ContactListMember>> {
    const params = new URLSearchParams();

    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.member_type) params.append('member_type', filters.member_type);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());

    const response = await api.get(`/contact_lists/${id}/members?${params.toString()}`);
    return response.data;
  }

  // Add members to a contact list
  static async addMembers(id: number, members: MemberToAdd[]): Promise<ApiResponse<{ added: number; requested: number; message: string; errors?: string[] }>> {
    const response = await api.post(`/contact_lists/${id}/members`, { members });
    return response.data;
  }

  // Remove members from a contact list
  static async removeMembers(id: number, membershipIds: number[]): Promise<ApiResponse<{ removed: number; requested: number; message: string }>> {
    const response = await api.delete(`/contact_lists/${id}/members`, {
      data: { membership_ids: membershipIds }
    });
    return response.data;
  }

  // Import contacts directly into a contact list
  static async import(id: number, contacts: any[]): Promise<{ status: string; job_id: string; message: string; total: number }> {
    const response = await api.post(`/contact_lists/${id}/import`, { contacts });
    return response.data;
  }

  // Export contacts from a contact list (returns blob URL)
  static async export(id: number): Promise<Blob> {
    const response = await api.get(`/contact_lists/${id}/export`, {
      responseType: 'blob'
    });
    return response.data;
  }
}

export default ContactListsAPI;
